import React, { useState } from 'react';
import { Modal, Select, Table, Button, Alert, Typography, Divider } from 'antd';
import { InfoCircleOutlined, ArrowRightOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

const FieldMappingModal = ({ 
  visible, 
  onCancel, 
  onConfirm, 
  headers, 
  loading 
}) => {
  console.log('FieldMappingModal render - visible:', visible, 'headers:', headers);
  
  const [mappings, setMappings] = useState({
    user: '',
    amount: '',
    type: '',
    date: '',
    before: '',
    after: '',
    credit: '',
    gst_amount: '',
    sales_no: '',
    reference_no: '',
    payment_mode: '',
    remarks: ''
  });

  const requiredFields = [
    { key: 'user', label: 'User', description: 'Username or user identifier' },
    { key: 'amount', label: 'Amount', description: 'Recharge amount' },
    { key: 'type', label: 'Type', description: 'Recharge type (e.g., CREDIT, DEBIT)' },
  ];

  const optionalFields = [
    { key: 'date', label: 'Recharge Date', description: 'Date of recharge transaction' },
    { key: 'before', label: 'Before Balance', description: 'Account balance before recharge' },
    { key: 'after', label: 'After Balance', description: 'Account balance after recharge' },
    { key: 'credit', label: 'Credit', description: 'Credit amount if applicable' },
    { key: 'gst_amount', label: 'GST Amount', description: 'GST charged on recharge' },
    { key: 'sales_no', label: 'Sales No', description: 'Sales reference number' },
    { key: 'reference_no', label: 'Reference No', description: 'Transaction reference' },
    { key: 'payment_mode', label: 'Payment Mode', description: 'Payment method used' },
    { key: 'remarks', label: 'Remarks', description: 'Additional notes' },
  ];

  const handleMappingChange = (field, value) => {
    setMappings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConfirm = () => {
    // Validate required fields
    const missingRequired = requiredFields.filter(f => 
      f.required && !mappings[f.key]
    );
    
    if (missingRequired.length > 0) {
      Modal.error({
        title: 'Missing Required Fields',
        content: `Please map the following required fields: ${missingRequired.map(f => f.label).join(', ')}`,
      });
      return;
    }

    onConfirm(mappings);
  };

  const columns = [
    {
      title: 'System Field',
      dataIndex: 'label',
      key: 'label',
      width: 200,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {text}
            {record.required && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
            {record.description}
          </div>
        </div>
      ),
    },
    {
      title: '',
      width: 50,
      render: () => <ArrowRightOutlined style={{ color: '#999' }} />,
    },
    {
      title: 'Uploaded Excel Field',
      key: 'mapping',
      render: (_, record) => (
        <Select
          style={{ width: '100%' }}
          placeholder="Select column"
          value={mappings[record.key] || undefined}
          onChange={(value) => handleMappingChange(record.key, value)}
          allowClear
          showSearch
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {headers.map((header, index) => (
            <Option key={index} value={header}>
              {header || `Column ${index + 1}`}
            </Option>
          ))}
        </Select>
      ),
    },
  ];

  const allFields = [
    { key: 'divider1', label: 'Required Fields', isDivider: true },
    ...requiredFields,
    { key: 'divider2', label: 'Optional Fields', isDivider: true },
    ...optionalFields
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Map Excel Columns to System Fields</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm} loading={loading}>
          Confirm & Upload
        </Button>,
      ]}
    >
      <Alert
        message="Field Mapping Required"
        description="Please map your Excel columns to the required system fields. Fields marked with * are required for successful import."
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 16 }}
      />

      <div style={{ marginBottom: 16 }}>
        <Title level={5} style={{ marginBottom: 8 }}>Detected Excel Headers:</Title>
        <Text code style={{ fontSize: '12px' }}>
          {headers.join(', ') || 'No headers detected'}
        </Text>
      </div>

      <Table
        columns={columns}
        dataSource={allFields}
        pagination={false}
        size="small"
        rowKey="key"
        rowClassName={(record) => record.isDivider ? 'divider-row' : ''}
        onHeaderRow={() => ({ style: { display: 'none' } })}
      />

      <style jsx>{`
        .divider-row {
          background: #f5f5f5;
          font-weight: 600;
        }
        .divider-row td {
          padding: 8px 16px !important;
          border-bottom: 1px solid #e8e8e8;
        }
      `}</style>
    </Modal>
  );
};

export default FieldMappingModal;
